import Button from "components/form/Button";
import InputField from "components/form/InputField";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  downloadPdfFromApi,
  encode,
  formatCurrency,
  validateEmail,
} from "utils/HelperFunctions";
import Drawer from "components/ui-component/SideDrawer";
import PaymentPreview from "components/payments/PaymentPreview";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { SuccessTick } from "icons/auth/SuccessTick";
import {
  paymentReceiptDownloadPdf,
  receiptIdGet,
} from "store/slices/paymentsSlice";
import { usePortalVanityUrl } from "store/slices/dashboardSlice";
import { useMediaQuery } from "react-responsive";
import MessageToast from "components/ui-component/MessageToast";
import { RootState } from "store/store";

interface OneTimeSuccessProps {
  email: string;
  amount: string | number;
  sendEmail: (val: any) => void;
  invoiceId: any;
  title?: string;
  receiptEmailSent?: boolean;
  isRetry?: boolean;
  isRecurring?: boolean;
  clusterId: string | number;
  lead_id: any;
  showSuccessToast?: boolean;
}

const OneTimeSuccess = ({
  email,
  amount,
  invoiceId,
  title,
  receiptEmailSent = true,
  sendEmail,
  isRetry = false,
  isRecurring = false,
  clusterId,
  lead_id,
  showSuccessToast = false,
}: OneTimeSuccessProps) => {
  const { companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [emailInput, setEmailInput] = useState("");
  const [id, setId] = useState(invoiceId);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [receiptId, setReceiptId] = useState<any>(null);
  const [isRecieptDownloaded, setIsRecieptDownloaded] =
    useState<boolean>(false);
  const [msg, setMsg] = useState<any>({
    variant: "",
    content: null,
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px" });
  const cluster_id = encode(`${clusterId}`, "ci_");
  const vanityUrl = usePortalVanityUrl();

  const handleSendEmail = async () => {
    setIsEmailLoading(true);
    if (!validateEmail(emailInput)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      setIsEmailLoading(false);
      return;
    }
    setIsEmailLoading(true);
    try {
      let emails = [emailInput];
      let value = {
        invoice_id: invoiceId,
        emails: emails,
        isRealTime: true,
        shouldSendAll: false,
      };
      await sendEmail(value);
      if (showSuccessToast) {
        setIsEmailSent(true);
        setMsg({
          ...msg,
          variant: "success",
          content: "Receipt sent successfully",
        });
      }
      setEmailInput("");
    } catch (error) {
      console.error("Failed to send email:", error);
      if (showSuccessToast) {
        setIsEmailSent(false);
        setMsg({
          variant: "error",
          content: "Could not send the receipt. Please try again.",
        });
      }
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleGetReceiptID = async (params: any) => {
    try {
      const response: any = await dispatch(receiptIdGet(params)).unwrap();
      if (response?.body?.success) {
        setReceiptId(response.body.data?.id);
        return response.body.data?.id;
      }
      return params?.orders_id;
    } catch (error) {
      console.error("Failed to get receipt ID:", error);
    }
  };

  const handleDownload = async () => {
    let receipt_id = receiptId;
    if (!receipt_id) {
      receipt_id = await handleGetReceiptID({
        orders_id: invoiceId,
      });
    }

    await onPaymentDownload(invoiceId, receipt_id);
  };

  const onPaymentDownload = async (id: any, receiptId?: any) => {
    let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
    let payload = {
      invoice_id: id,
      lead_id: lead_id,
      url: `${vanityUrl}view-payment/${pid}`,
      cluster_id: clusterId,
      isFromPortal: true,
    };

    // downloadPdfFromApi reports failure by returning false rather than
    // throwing, so the result has to be read — an unchecked call announced
    // "downloaded successfully" even when nothing had been downloaded.
    const downloaded = await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      `Receipt - ${receiptId || id}.pdf`,
      setIsDownloadLoading
    );

    if (showSuccessToast) {
      setIsRecieptDownloaded(downloaded);
      setMsg({
        ...msg,
        variant: downloaded ? "success" : "error",
        content: downloaded
          ? "Receipt downloaded successfully"
          : "Could not download the receipt. Please try again.",
      });
    }
  };

  return (
    <div
      className={`w-full h-full relative lg:py-0 py-5 ${
        !isRetry && "max-w-[620px] px-5"
      } `}
    >
      <div className="h-full flex flex-col justify-center items-center gap-24">
        <div className="w-full flex flex-col justify-center items-center gap-10">
          <SuccessTick />
          <div className="text-txtPrimary lg:text-2xl lg:leading-8 text-xl leading-[26.04px] font-medium">
            {isRecurring
              ? "Subscription started successfully"
              : title || "Payment completed"}
          </div>

          <div className="w-full flex flex-col justify-center items-center">
            {isRecurring ? (
              <div className="w-full text-center bg-bgThird lg:py-5 lg:px-2 py-4 px-3 lg:text-xl text-base text-txtParagraph mb-1 font-normal">
                Your payment of{" "}
                <span className="font-bold">
                  {formatCurrency(amount) || "$0.00"}
                </span>{" "}
                has been received.
              </div>
            ) : (
              <div className="w-full flex justify-between items-end bg-bgThird lg:gap-4 gap-2 lg:py-5 lg:px-2 py-4 px-3 lg:text-xl text-base text-txtParagraph mb-1 font-bold">
                <p className="w-fit whitespace-nowrap">
                  {companyDetails?.name ||
                    companyDetails?.name ||
                    companyDetails?.company_name}
                </p>
                <div className="h-[1px] w-full bg-strokePrimary"></div>
                <span className="font-bold">
                  {formatCurrency(amount) || "$0.00"}
                </span>{" "}
              </div>
            )}
          </div>
        </div>
        <div className="w-full relative">
          <div className="flex-col flex justify-center items-center lg:gap-[30px] gap-5 w-full">
            <p className="lg:text-base text-sm text-center text-txtSecondary lg:px-5">
              {receiptEmailSent ? (
                <>
                  A copy of this receipt will automatically be sent to{" "}
                  <span className="font-bold">{email}</span>. To send a copy to an
                  additional email, enter the address below.
                </>
              ) : (
                <>
                  We couldn&apos;t send the receipt to{" "}
                  <span className="font-bold">{email}</span>. Enter an email address
                  below to try again.
                </>
              )}
            </p>
            <div className="px-[10px] w-full">
              <InputField
                type="email"
                name="email"
                label=""
                placeholder="Enter your email address"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setIsErr(false);
                  setErrMsg(undefined);
                }}
                className="!py-3"
                error={errMsg}
                isErr={isErr}
              />
            </div>
            <div className="m-[10px] w-full flex justify-center items-center gap-5 px-[10px]">
              <Button
                text="Send Email"
                className="w-full"
                isLoading={isEmailLoading}
                onClick={handleSendEmail}
                isDisabled={emailInput.length === 0 && !isEmailSent}
                isSuccess={isEmailSent}
              />
              <Button
                isSuccess={isRecieptDownloaded}
                text="Download"
                className="w-full"
                isLoading={isDownloadLoading}
                onClick={() => handleDownload()}
              />
            </div>
          </div>
          <div className="w-full mt-6  lg:text-base text-sm text-center text-btnPrimaryActive hover:!text-btnPrimaryHover ">
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/${cluster_id}/dashboard`)}
            >
              Back to dashboard
            </div>
          </div>
        </div>
      </div>
      <Drawer
        enableOverlay={true}
        lockBackgroundScroll={true}
        direction="right"
        open={isPreview}
        size={isLessThan590 ? `100vw` : "590px"}
        onClose={() => {
          setIsPreview(false);
          setId("");
        }}
        overlayColor="#000"
      >
        <PaymentPreview
          id={id}
          setId={setId}
          show={isPreview}
          setShow={setIsPreview}
          onDownloadCLick={(id: any, receiptId?: any) =>
            onPaymentDownload(id, receiptId)
          }
          isDownloading={isDownloadLoading.toString()}
        />
      </Drawer>
      <div className="relative max-w-[560px] !bottom-0 z-[9999999]">
        <MessageToast
          variant={msg?.variant}
          content={msg?.content}
          isProgressBar
          width="max-w-[580px] w-full px-5"
          className=" !absolute !right-1/2 !translate-x-1/2"
          onCloseClick={() => {
            setMsg({
              ...msg,
              variant: "",
              content: null,
            });
            setIsEmailSent(false);
            setIsRecieptDownloaded(false);
          }}
        />
      </div>
    </div>
  );
};

export default OneTimeSuccess;
